/*
 * Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved.
 *
 * This code and associated materials are protected by copyright laws and
 * international treaties. Unauthorized use, reproduction, distribution, or
 * modification of any content from Knowledge for Decisions (K4D) is strictly
 * prohibited. For inquiries regarding the use or licensing of K4D's
 * copyrighted materials, please contact us at contact@k4d.com.br. "Knowledge
 * for Decisions (K4D)" and the slogan "Using data to make precise decisions"
 * are registered trademarks of Knowledge for Decisions (K4D).
 */

import React from 'react';
import { useTranslation } from 'react-i18next';
import type { Calendar } from '@services/requests/housien-models/Calendar';
import type { ExpandedEvent } from '@features/agenda/utils/expand-recurrence';
import { eventColor } from './event-color';
import { Text, UnstyledButton } from '@/shared/ui';

/**
 * Typography for all-day chips, applied inline — the shared `Text` component
 * ships classes that outrank `truncate`/`font-bold` at equal specificity.
 */
const NOWRAP = { whiteSpace: 'nowrap', fontWeight: 700 } as const;

interface AllDayChipProps {
  event: ExpandedEvent;
  calendars: Calendar[];
  onSelect: (event: ExpandedEvent) => void;
}

/**
 * One all-day event rendered as a full-width pill inside the band.
 * @param props - The event, calendars for colour resolution, click handler.
 * @returns The chip element.
 */
export const AllDayChip: React.FC<AllDayChipProps> = ({ event, calendars, onSelect }) => {
  const { t } = useTranslation('features/agenda');
  const color = eventColor(event, calendars);
  return (
    <UnstyledButton
      type="button"
      data-testid="agenda-all-day-chip"
      title={`${t('drawer.all_day')} · ${event.title}`}
      onClick={() => onSelect(event)}
      className="block w-full min-w-0 overflow-hidden rounded-[var(--radius-sm)] border-l-[3px] px-(--space-1-5) py-(--space-0-5) text-left transition-shadow hover:shadow-[var(--shadow-md)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--seed-base)]"
      style={{
        background: `color-mix(in srgb, ${color} 12%, var(--surface-primary))`,
        borderColor: color,
      }}
    >
      <Text
        component="span"
        style={{ color, ...NOWRAP }}
        className="block w-full truncate text-xs leading-tight"
      >
        {event.title}
      </Text>
    </UnstyledButton>
  );
};

/**
 * Left-hand gutter label of the all-day band, aligned with the hour column.
 * @param props - Optional extra classes for width/padding alignment.
 * @returns The label element.
 */
export const AllDayLabel: React.FC<{ className?: string }> = ({ className = '' }) => {
  const { t } = useTranslation('features/agenda');
  return (
    <div className={`flex items-start justify-end pt-(--space-1) ${className}`}>
      <Text
        component="span"
        className="text-right text-(--text-eyebrow) font-semibold uppercase leading-tight text-[var(--text-tertiary)]"
      >
        {t('drawer.all_day')}
      </Text>
    </div>
  );
};

/**
 * Scroll wrapper shared by the day and week bands: keeps the band compact and
 * scrollable on its own once more than a couple of all-day events pile up, so
 * it never pushes the hour grid off screen.
 */
export const ALL_DAY_SCROLL =
  'max-h-[76px] overflow-y-auto flex flex-col gap-(--space-0-5)';
